import { motion } from "framer-motion";
import { Flame, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { fadeUp } from "@/lib/motion";

/**
 * Stage 2.2: gentle streak-protection reminder.
 * Shows ONLY when the user has an active streak (>= 1 day, earned up to
 * yesterday) and has not logged any activity today. Never shown to brand-new
 * users without a streak. Disappears automatically once an activity is added
 * (stats reload flips today_has_activity).
 */
export const QuietDayNudge = ({ stats, loading, onAdd }) => {
  const { t } = useLanguage();

  if (loading || !stats) return null;
  if (stats.today_has_activity || stats.current_streak < 1) return null;

  const days = `${stats.current_streak} ${
    stats.current_streak === 1 ? t("common.day") : t("common.days")
  }`;

  return (
    <motion.div {...fadeUp(0.09)}>
      <GlassCard className="p-4" data-testid="quiet-day-nudge">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center">
            <Flame size={18} className="text-amber-400/90" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{t("nudge.title")}</p>
            <p
              data-testid="quiet-day-nudge-message"
              className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed"
            >
              {t("nudge.message", { days })}
            </p>
          </div>
          <Button
            data-testid="quiet-day-nudge-add-button"
            onClick={onAdd}
            size="sm"
            className="shrink-0 rounded-lg h-9 px-3 text-xs font-semibold bg-cyan-300 text-slate-950 hover:bg-cyan-200 active:scale-[0.97]"
          >
            <Plus size={14} className="mr-1" /> {t("common.addMovement")}
          </Button>
        </div>
      </GlassCard>
    </motion.div>
  );
};
